'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function InflationCalculator() {
  const t = useTranslations("tools.inflationCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // CPI Data (Consumer Price Index for All Urban Consumers)
  const cpiData: { [key: number]: number } = {
    2025: 315.5, 2024: 310.3, 2023: 304.7, 2022: 292.7, 2021: 271.0, 2020: 258.8,
    2019: 255.7, 2018: 251.1, 2017: 245.1, 2016: 240.0, 2015: 237.0, 2014: 236.7,
    2013: 233.0, 2012: 229.6, 2011: 224.9, 2010: 218.1, 2009: 214.5, 2008: 215.3,
    2007: 207.3, 2006: 201.6, 2005: 195.3, 2004: 188.9, 2003: 184.0, 2002: 179.9,
    2001: 177.1, 2000: 172.2, 1999: 166.6, 1998: 163.0, 1997: 160.5, 1996: 156.9,
    1995: 152.4, 1994: 148.2, 1993: 144.5, 1992: 140.3, 1991: 136.2, 1990: 130.7,
    1989: 124.0, 1988: 118.3, 1987: 113.6, 1986: 109.6, 1985: 107.6, 1984: 103.9,
    1983: 99.6, 1982: 96.5, 1981: 90.9, 1980: 82.4, 1979: 72.6, 1978: 65.2,
    1977: 60.6, 1976: 56.9, 1975: 53.8, 1974: 49.3, 1973: 44.4, 1972: 41.8,
    1971: 40.5, 1970: 38.8, 1969: 36.7, 1968: 34.8, 1967: 33.4, 1966: 32.4,
    1965: 31.5, 1964: 31.0, 1963: 30.6, 1962: 30.2, 1961: 29.9, 1960: 29.6,
    1959: 29.1, 1958: 28.9, 1957: 28.1, 1956: 27.2, 1955: 26.8, 1954: 26.9,
    1953: 26.7, 1952: 26.5, 1951: 26.0, 1950: 24.1, 1949: 23.8, 1948: 24.1,
    1947: 22.3, 1946: 19.5, 1945: 18.0, 1944: 17.6, 1943: 17.3, 1942: 16.3,
    1941: 14.7, 1940: 14.0, 1939: 13.9, 1938: 14.1, 1937: 14.4, 1936: 13.7,
    1935: 13.7, 1934: 13.4, 1933: 12.9, 1932: 13.7, 1931: 15.2, 1930: 16.7,
    1929: 17.1, 1928: 17.1, 1927: 17.3, 1926: 17.7, 1925: 17.5, 1924: 17.1,
    1923: 17.1, 1922: 16.8, 1921: 17.9, 1920: 20.0, 1919: 17.3, 1918: 15.1,
    1917: 12.8, 1916: 10.9, 1915: 10.1, 1914: 10.0, 1913: 9.9
  };

  const years = Object.keys(cpiData).sort((a, b) => parseInt(b) - parseInt(a));

  // Calculator state
  const [inputs, setInputs] = useState({
    amount: 100,
    startYear: 2015,
    endYear: 2025
  });

  const [results, setResults] = useState<{
    adjustedAmount: number;
    totalInflation: number;
    annualRate: number;
    purchasingPowerChange: number;
    yearsDiff: number;
  } | null>(null);

  const [error, setError] = useState('');

  // Screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Responsive style helper
  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Calculate inflation
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { amount, startYear, endYear } = inputs;

    if (amount <= 0) {
      setError(t('errorPositiveAmount'));
      setResults(null);
      return;
    }

    if (startYear === endYear) {
      setError(t('errorDifferentYears'));
      setResults(null);
      return;
    }

    const startCPI = cpiData[startYear];
    const endCPI = cpiData[endYear];

    const adjustedAmount = (amount * endCPI) / startCPI;
    const totalInflation = ((endCPI - startCPI) / startCPI) * 100;
    const yearsDiff = Math.abs(endYear - startYear);
    const annualRate = (Math.pow(endCPI / startCPI, 1 / yearsDiff) - 1) * 100;
    const purchasingPowerChange = ((startCPI - endCPI) / endCPI) * 100;

    setResults({
      adjustedAmount,
      totalInflation,
      annualRate,
      purchasingPowerChange,
      yearsDiff
    });
  };

  // Auto-calculate on input change
  useEffect(() => {
    if (results) {
      const form = document.getElementById('calc-form') as HTMLFormElement;
      if (form) {
        handleCalculate(new Event('submit') as any);
      }
    }
  }, [inputs]);

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format percentage
  const formatPercent = (value: number) => {
    return (value >= 0 ? '+' : '') + value.toFixed(2) + '%';
  };

  // Colors
  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1d4ed8',
    purple: '#7c3aed',
    green: '#22c55e',
    greenDark: '#166534',
    greenLight: '#f0fdf4',
    greenLighter: '#dcfce7',
    red: '#991b1b',
    redLight: '#fef2f2',
    redBorder: '#fecaca',
    gray50: '#f8fafc',
    gray100: '#f1f5f9',
    gray200: '#e2e8f0',
    gray300: '#cbd5e1',
    gray400: '#94a3b8',
    gray500: '#64748b',
    gray600: '#475569',
    gray700: '#334155',
    gray800: '#1e293b',
  };

  // Styles
  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: 0 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif'
    },
    { padding: '2rem 1rem' },
    { padding: '2.5rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '900px',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const
    },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const formRowStyle = getResponsiveStyle(
    {
      display: 'grid',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '2rem',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: '0.75rem',
      border: `2px solid ${colors.gray200}`
    },
    { gridTemplateColumns: '1fr', gap: '1rem' },
    { gridTemplateColumns: '1fr auto 1fr', gap: '1.5rem' },
    { gridTemplateColumns: '1fr auto 1fr', gap: '1.5rem' }
  );

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem'
  };

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.gray700 },
    { fontSize: '0.85rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' }
  );

  const inputWrapperStyle = {
    position: 'relative' as const
  };

  const currencySymbolStyle = getResponsiveStyle(
    {
      position: 'absolute' as const,
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.gray500,
      fontWeight: 600
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `2px solid ${colors.gray300}`,
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      fontWeight: 600
    },
    { padding: '0.75rem 1rem 0.75rem 2.5rem', fontSize: '1rem' },
    { padding: '0.8125rem 1rem 0.8125rem 2.5rem', fontSize: '1.05rem' },
    { padding: '0.875rem 1rem 0.875rem 2.5rem', fontSize: '1.1rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `2px solid ${colors.gray300}`,
      borderRadius: '0.5rem',
      background: 'white',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    { padding: '0.75rem 1rem', fontSize: '0.95rem' },
    { padding: '0.8125rem 1rem', fontSize: '0.975rem' },
    { padding: '0.875rem 1rem', fontSize: '1rem' }
  );

  const equalsSignStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.blue, textAlign: 'center' as const },
    { fontSize: '1.5rem', display: 'none' },
    { fontSize: '1.75rem', display: 'block' },
    { fontSize: '2rem', display: 'block' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.75rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.9375rem 1.75rem', fontSize: '1.05rem' },
    { padding: '1rem 2rem', fontSize: '1.1rem' }
  );

  const errorMessageStyle = {
    background: colors.redLight,
    border: `2px solid ${colors.redBorder}`,
    color: colors.red,
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: error ? 'block' : 'none'
  };

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: `3px solid ${colors.green}`,
      display: results ? 'block' : 'none'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    {
      margin: '0 0 1.5rem 0',
      color: colors.greenDark,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const resultMainStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultHighlightStyle = getResponsiveStyle(
    {
      fontWeight: 800,
      color: colors.green,
      margin: '0.5rem 0'
    },
    { fontSize: '2rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const resultDescriptionStyle = getResponsiveStyle(
    {
      color: colors.gray600,
      lineHeight: 1.6
    },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' },
    { fontSize: '1.1rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    {
      display: 'grid',
      gap: '1rem',
      marginTop: '1.5rem'
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      borderLeft: `4px solid ${colors.blue}`,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1rem' },
    { padding: '1.125rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle = {
    display: 'block',
    fontSize: '0.85rem',
    color: colors.gray500,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.5rem'
  };

  const resultValueStyle = getResponsiveStyle(
    {
      display: 'block',
      fontWeight: 700,
      color: colors.gray800
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
      marginTop: 0
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.75rem',
      marginTop: '1.5rem'
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    {
      lineHeight: 1.7,
      color: colors.textSecondary,
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    {
      lineHeight: 1.8,
      color: colors.textSecondary,
      paddingLeft: '1.5rem',
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const faqItemStyle = {
    marginBottom: '1.5rem'
  };

  const faqQuestionStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem'
    },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.1875rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    {
      lineHeight: 1.7,
      color: colors.textSecondary
    },
    { fontSize: '0.9375rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    transition: 'color 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>💰 {t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} id="calc-form" onSubmit={handleCalculate}>
            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="amount" style={labelStyle}>{t('amountLabel')}</label>
                <div style={inputWrapperStyle}>
                  <span style={currencySymbolStyle}>$</span>
                  <input
                    type="number"
                    id="amount"
                    step="0.01"
                    min="0"
                    value={inputs.amount}
                    onChange={(e) => setInputs({...inputs, amount: parseFloat(e.target.value) || 0})}
                    required
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={equalsSignStyle}>=</div>

              <div style={formGroupStyle}>
                <label htmlFor="result-display" style={labelStyle}>{t('equivalentValueLabel')}</label>
                <div style={inputWrapperStyle}>
                  <span style={currencySymbolStyle}>$</span>
                  <input
                    type="text"
                    id="result-display"
                    readOnly
                    style={{...inputStyle, background: colors.gray50, cursor: 'not-allowed'}}
                    value={results ? formatCurrency(results.adjustedAmount).replace('$', '') : '?'}
                  />
                </div>
              </div>
            </div>

            <div style={formRowStyle}>
              <div style={formGroupStyle}>
                <label htmlFor="start-year" style={labelStyle}>{t('startYearLabel')}</label>
                <select
                  id="start-year"
                  value={inputs.startYear}
                  onChange={(e) => setInputs({...inputs, startYear: parseInt(e.target.value)})}
                  required
                  style={selectStyle}
                >
                  {years.map(year => (
                    <option key={`start-${year}`} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div style={equalsSignStyle}>→</div>

              <div style={formGroupStyle}>
                <label htmlFor="end-year" style={labelStyle}>{t('endYearLabel')}</label>
                <select
                  id="end-year"
                  value={inputs.endYear}
                  onChange={(e) => setInputs({...inputs, endYear: parseInt(e.target.value)})}
                  required
                  style={selectStyle}
                >
                  {years.map(year => (
                    <option key={`end-${year}`} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
            <div style={errorMessageStyle}>{error}</div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={resultMainStyle}>
                <div style={resultDescriptionStyle}>
                  {t('resultDescription', { amount: inputs.amount.toFixed(2), startYear: inputs.startYear })}
                </div>
                <div style={resultHighlightStyle}>{formatCurrency(results.adjustedAmount)}</div>
              </div>

              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('totalInflationLabel')}</span>
                  <span style={resultValueStyle}>{formatPercent(results.totalInflation)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('annualRateLabel')}</span>
                  <span style={resultValueStyle}>{formatPercent(results.annualRate)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('purchasingPowerLabel')}</span>
                  <span style={resultValueStyle}>{formatPercent(results.purchasingPowerChange)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('timePeriodLabel')}</span>
                  <span style={resultValueStyle}>{results.yearsDiff} {t('yearsUnit')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li>{t('howToUseStep1')}</li>
          <li>{t('howToUseStep2')}</li>
          <li>{t('howToUseStep3')}</li>
          <li>{t('howToUseStep4')}</li>
        </ol>
        <p style={paragraphStyle}>{t('howToUseNote')}</p>
      </section>

      {/* Key Insights */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insightsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li><strong style={strongStyle}>{t('insightsCPILabel')}</strong> {t('insightsCPIText')}</li>
          <li><strong style={strongStyle}>{t('insightsSeasonalLabel')}</strong> {t('insightsSeasonalText')}</li>
          <li><strong style={strongStyle}>{t('insightsMarketBasketLabel')}</strong> {t('insightsMarketBasketText')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <ul style={listStyle}>
          <li><strong style={strongStyle}>{t('insightsInflationRateLabel')}</strong> {t('insightsInflationRateText')}</li>
          <li><strong style={strongStyle}>{t('insightsAdjustedAmountLabel')}</strong> {t('insightsAdjustedAmountText')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsSubtitle3')}</h3>
        <ul style={listStyle}>
          <li><strong style={strongStyle}>{t('insightsCOLALabel')}</strong> {t('insightsCOLAText')}</li>
          <li><strong style={strongStyle}>{t('insightsSalaryLabel')}</strong> {t('insightsSalaryText')}</li>
          <li><strong style={strongStyle}>{t('insightsInvestmentLabel')}</strong> {t('insightsInvestmentText')}</li>
          <li><strong style={strongStyle}>{t('insightsPolicyLabel')}</strong> {t('insightsPolicyText')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <ul style={listStyle}>
          <li><strong style={strongStyle}>{t('detailsHeadlineCPILabel')}</strong> {t('detailsHeadlineCPIText')}</li>
          <li><strong style={strongStyle}>{t('detailsCoreCPILabel')}</strong> {t('detailsCoreCPIText')}</li>
          <li><strong style={strongStyle}>{t('detailsPCELabel')}</strong> {t('detailsPCEText')}</li>
        </ul>

        <h3 style={h3Style}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <ul style={listStyle}>
          <li>{t('detailsLimitation1')}</li>
          <li>{t('detailsLimitation2')}</li>
          <li>{t('detailsLimitation3')}</li>
        </ul>

        <h3 style={h3Style}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
        <ul style={listStyle}>
          <li>{t('detailsUseCase1')}</li>
          <li>{t('detailsUseCase2')}</li>
          <li>{t('detailsUseCase3')}</li>
          <li>{t('detailsUseCase4')}</li>
          <li>{t('detailsUseCase5')}</li>
        </ul>
      </section>

      {/* FAQ */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq1Question')}</div>
          <div style={faqAnswerStyle}>{t('faq1Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq2Question')}</div>
          <div style={faqAnswerStyle}>{t('faq2Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq3Question')}</div>
          <div style={faqAnswerStyle}>{t('faq3Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq4Question')}</div>
          <div style={faqAnswerStyle}>{t('faq4Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq5Question')}</div>
          <div style={faqAnswerStyle}>{t('faq5Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq6Question')}</div>
          <div style={faqAnswerStyle}>{t('faq6Answer')}</div>
        </div>
      </section>

      {/* References */}
      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li>
            <a href="https://www.bls.gov/cpi/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference1')}
            </a>
          </li>
          <li>
            <a href="https://www.bls.gov/data/inflation_calculator.htm" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference2')}
            </a>
          </li>
          <li>
            <a href="https://www.ssa.gov/cola/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference3')}
            </a>
          </li>
          <li>
            <a href="https://www.federalreserve.gov/faqs/economy_14419.htm" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference4')}
            </a>
          </li>
          <li>
            <a href="https://www.investopedia.com/terms/i/inflation.asp" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}